(() => {
  if (customElements.get('lc-bulb-context')) return;
  customElements.define('lc-bulb-context', class extends HTMLElement {
    async connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      await customElements.whenDefined('product-info');
      if (!this.isConnected || generation !== this.generation || this.initialized) return;
      this.initialized = true;
      this.info = this.querySelector('product-info');
      if (!this.info) return;
      if (this.hasAttribute('data-enhanced-navigation')) {
        this.relatedReady = this.loadRelatedProducts();
        this.navigationController = new AbortController();
        this.addEventListener('click', this.onTechnicalLink.bind(this), { signal: this.navigationController.signal });
      }
      this.originalBuild = this.info.buildRequestUrlWithParams;
      this.originalUpdate = this.info.updateURL;
      this.originalQuantity = this.info.fetchQuantityRules;
      const context = this;
      this.info.buildRequestUrlWithParams = function (...args) {
        const url = new URL(context.originalBuild.apply(this, args), location.origin);
        url.searchParams.set('view', context.dataset.view);
        return url.pathname + url.search;
      };
      this.info.updateURL = function (...args) {
        context.originalUpdate.apply(this, args);
        if (this.dataset.updateUrl === 'false') return;
        const url = new URL(location.href);
        url.searchParams.set('view', context.dataset.view);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      };
      this.info.fetchQuantityRules = function () {
        const variantId = this.productForm?.productIdInput?.value;
        if (!variantId) return;
        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.sectionId);
        url.searchParams.set('view', context.dataset.view);
        return fetch(url.pathname + url.search)
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, html);
          })
          .catch(error => console.error(error))
          .finally(() => this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading'));
      };
      this.info.cartUpdateUnsubscriber?.();
      this.info.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.info.fetchQuantityRules.bind(this.info)
      );
      this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.section || !data.html) return;
        for (const prefix of ['LCBulbHighlights', 'LCBulbTechnical', 'LCBulbTrust', 'LCBulbFacts']) {
          const id = `${prefix}-${this.dataset.section}`;
          const source = data.html.getElementById(id);
          const destination = this.querySelector(`#${id}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.hidden = source.hidden;
          }
        }
      });
    }
    async loadRelatedProducts() {
      const module = this.querySelector('[data-lc-related]');
      const template = module?.querySelector('[data-lc-related-carousel]');
      if (!module || !template) return;
      const generation = this.generation;
      const controller = this.relatedAbort = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(module.dataset.url, location.origin);
        if (url.origin !== location.origin) return;
        const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) return;
        const source = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = [...source.querySelectorAll('product-recommendations.related-products .products-grid > .f-column .product-card')].slice(0, 10);
        if (!cards.length || !this.isConnected || generation !== this.generation) return;
        if (!customElements.get('product-slider')) return;
        const content = template.content.cloneNode(true);
        const slider = content.querySelector('product-slider');
        const grid = content.querySelector('[data-lc-related-grid]');
        const controls = content.querySelector('[data-lc-related-controls]');
        const fragment = document.createDocumentFragment();
        for (const sourceCard of cards) {
          const card = sourceCard.cloneNode(true);
          card.querySelectorAll('.product-card__quickview,.product-card__actions,.product-card__list-actions,.product-card__swatches,.swatches,quick-view-modal,color-swatch,[is="color-swatch"],form,button,input,select,textarea,script,style').forEach(node => node.remove());
          card.querySelectorAll('a[aria-controls]').forEach(link => {
            if (link.getAttribute('aria-controls').startsWith('QuickView-')) {
              link.removeAttribute('aria-controls');
              link.removeAttribute('aria-haspopup');
            }
          });
          card.querySelectorAll('motion-element').forEach(motion => {
            const wrapper = document.createElement('div');
            wrapper.className = motion.className;
            wrapper.append(...motion.childNodes);
            motion.replaceWith(wrapper);
          });
          const column = document.createElement('div');
          column.className = 'f-column swiper-slide lc-cards-collection';
          column.append(card);
          fragment.append(column);
        }
        grid.replaceChildren(fragment);
        slider.dataset.totalItems = String(cards.length);
        controls.classList.toggle('md:flex', cards.length > 3);
        controls.classList.toggle('lg:flex', cards.length > 4);
        controls.classList.toggle('lg:hidden', cards.length <= 4);
        const previousLabel = controls.querySelector('.swiper-button-prev').getAttribute('aria-label');
        const nextLabel = controls.querySelector('.swiper-button-next').getAttribute('aria-label');
        const nativeInitSlider = customElements.get('product-slider').prototype.initSlider;
        slider.initSlider = function () {
          nativeInitSlider.call(this);
          const instance = this.sliderInstance?.slider;
          if (!instance) return;
          Object.assign(instance.params.a11y, {
            prevSlideMessage: previousLabel,
            nextSlideMessage: nextLabel,
            firstSlideMessage: '',
            lastSlideMessage: ''
          });
          this.querySelector('.swiper-button-prev').setAttribute('aria-label', previousLabel);
          this.querySelector('.swiper-button-next').setAttribute('aria-label', nextLabel);
        };
        slider.handleAccessibility = () => {
          if (slider.dataset.lcFocusReady) return;
          slider.dataset.lcFocusReady = 'true';
          slider.addEventListener('focusin', event => {
            const slide = event.target.closest('.swiper-slide');
            const instance = slider.sliderInstance?.slider;
            if (!slide || !instance) return;
            const index = Array.from(instance.slides).indexOf(slide);
            if (index >= 0) instance.slideTo(index, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : undefined);
          }, { signal: this.navigationController.signal });
        };
        slider.addEventListener('keydown', event => {
          const instance = slider.sliderInstance?.slider;
          if (!instance || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const speed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : undefined;
          if (event.key === 'Home') instance.slideTo(0, speed);
          else if (event.key === 'End') instance.slideTo(instance.slides.length - 1, speed);
          else if (event.key === 'ArrowRight') instance.slideNext(speed);
          else instance.slidePrev(speed);
        }, { signal: this.navigationController.signal });
        module.querySelector('product-slider')?.remove();
        module.hidden = false;
        module.append(content);
        this.relatedSlider = slider;
      } catch {
        if (generation === this.generation) module.hidden = true;
      } finally {
        clearTimeout(timeout);
      }
    }
    async onTechnicalLink(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || !this.contains(link)) return;
      const url = new URL(link.href, location.href);
      const allowedIds = [`LCBulbTechnical-${this.dataset.section}`, `LCTechnical-${this.dataset.section}`];
      const targetId = decodeURIComponent(url.hash.slice(1));
      if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !allowedIds.includes(targetId)) return;
      const target = document.getElementById(targetId);
      if (!target || !this.contains(target)) return;
      event.preventDefault();
      const request = this.scrollRequest = (this.scrollRequest || 0) + 1;
      const startY = window.scrollY;
      await this.relatedReady;
      if (!this.isConnected || request !== this.scrollRequest || Math.abs(window.scrollY - startY) > 8) return;
      const header = document.querySelector('header[is="sticky-header"]');
      let headerHeight = header?.getBoundingClientRect().height || 0;
      const navigation = header?.querySelector('.header__navigation');
      if (header?.dataset.collapseOnScroll === 'true' && navigation && !navigation.classList.contains('is-show')) {
        headerHeight = Math.max(0, headerHeight - navigation.getBoundingClientRect().height);
      }
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - 16);
      const heading = target.querySelector('h2') || target;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, left: window.scrollX, behavior: reduced ? 'instant' : 'smooth' });
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.unsubscribe?.();
      this.navigationController?.abort();
      this.navigationController = null;
      this.relatedSlider?.destroySlider();
      this.relatedSlider = null;
      this.relatedAbort?.abort();
      this.relatedAbort = null;
      this.scrollRequest = (this.scrollRequest || 0) + 1;
      if (this.info && this.initialized) {
        this.info.buildRequestUrlWithParams = this.originalBuild;
        this.info.updateURL = this.originalUpdate;
        this.info.cartUpdateUnsubscriber?.();
        this.info.fetchQuantityRules = this.originalQuantity;
      }
      this.initialized = false;
    }
  });
})();
